import type { Ref } from 'vue'

interface StarDistribution {
  [key: number]: number
}

interface ReviewStatisticsData {
  averageRating: number
  starDistribution: StarDistribution
}

interface ReviewStatistics {
  data: ReviewStatisticsData
}

interface UseReviewStatisticsReturn {
  statistics: Ref<ReviewStatistics | null>
  isLoading: Ref<boolean>
  error: Ref<Error | null>
  totalRatings: ComputedRef<number>
  fetchReviewStatistics: () => Promise<void>
}

/**
 * Composable for fetching and managing review statistics
 * Uses shared state to ensure API is called only once globally
 * Performance-optimized for use in header without blocking render
 */
export const useReviewStatistics = (): UseReviewStatisticsReturn => {
  const { apiClient } = useShopwareContext()

  // Shared state across all instances - singleton pattern
  const statistics = useState<ReviewStatistics | null>('review-statistics', () => null)
  const isLoading = useState<boolean>('review-statistics-loading', () => false)
  const error = useState<Error | null>('review-statistics-error', () => null)
  const hasFetched = useState<boolean>('review-statistics-fetched', () => false)

  // Computed total ratings
  const totalRatings = computed(() => {
    const distribution = statistics.value?.data?.starDistribution || {}
    return Object.values(distribution).reduce((sum, count) => sum + count, 0)
  })

  /**
   * Fetch review statistics from API
   * Only fetches if not already loaded (singleton behavior)
   */
  const fetchReviewStatistics = async () => {
    // If already fetched or currently loading, skip
    if (hasFetched.value || isLoading.value) {
      return
    }

    isLoading.value = true
    error.value = null

    try {
      const response = await apiClient.invoke(
        'FetchReviewStatistics get /mysofabed/product-reviews/statistics',
        {}
      )

      if (response?.status === 200) {
        statistics.value = response?.data
        hasFetched.value = true
      } else {
        error.value = new Error('Unexpected response format')
      }
    } catch (err) {
      console.error('Failed to fetch reviews:', err)
      error.value = err instanceof Error ? err : new Error('Unknown error')
    } finally {
      isLoading.value = false
    }
  }

  return {
    statistics,
    isLoading,
    error,
    totalRatings,
    fetchReviewStatistics,
  }
}

